import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect, useCallback } from "react";
import Header from "../../../components/Header";
import { accountsmanagement, HRService } from "../../../config";
import {
  graphqlQuery,
  generateAndExecuteMutation,
  fetchDataEngine,
} from "../../../data/Axios/DynamicService";
import {
  allAccountSubCategories,
  allAccounts,
} from "../../../data/Axios/queries";

const base_url = accountsmanagement.uri;
const hr_base_url = HRService.uri

const normalizeExpenseType = (type) => ({
  ...type,
  id: Number(type.id),
  label:
    type.accountSubCategoryName ||
    type.accountCategoryName ||
    type.name ||
    `Expense Type ${type.id}`,
});

const normalizeAccount = (account) => ({
  ...account,
  id: Number(account.id),
  label: account.accountNumber
    ? `${account.accountName} (${account.accountNumber})`
    : account.accountName,
});

const StaffClaimApplicationLineForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  // Read parent Claim ID from localStorage (updated for claims consistency)
  const claimData = JSON.parse(localStorage.getItem("ClaimData") || "{}");
  const parentClaimId = claimData?.id || null;

  const [initialValues, setInitialValues] = useState({
    id: data?.id || null,
    applicationId: data?.applicationId || parentClaimId || "",
    expenseTypeId: data?.expenseTypeId || "",
    accountId: data?.accountId || "",
    description: data?.description || "",
    amount: data?.amount || 0,
    isActive: data?.isActive ?? 1,
  });

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch Expense Types (Account Subcategories filtered by allowed categories)
  useEffect(() => {
    const fetchExpenseTypes = async () => {
      try {
        const subCategoryData = await graphqlQuery(allAccountSubCategories, base_url);

        // Adjust allowedCategories based on your business logic
        const allowedCategories = [4, 11]; // Example: Expense-related categories

        const filtered = (subCategoryData || [])
          .filter((sub) =>
            allowedCategories.includes(Number(sub.accountCategoryId)) &&
            sub.isActive !== false &&
            sub.isActive !== 0
          )
          .map(normalizeExpenseType);

        setExpenseTypes(filtered);
      } catch (error) {
        console.error("Error fetching expense types:", error);
      }
    };

    fetchExpenseTypes();
  }, []);

  const loadAccountsForExpenseType = useCallback(async (expenseTypeId) => {
    if (!expenseTypeId) {
      setAccounts([]);
      return;
    }

    try {
      const accountData = await fetchDataEngine(
        "allAccountsByColumnValues",
        "accounts",
        { accountSubcategoryId: Number(expenseTypeId) },
        ["id", "accountNumber", "accountName"],
        base_url
      );

      setAccounts((accountData || []).map(normalizeAccount));
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    }
  }, []);

  // Fetch Accounts when Expense Type changes
  const handleExpenseTypeChange = async (event, setFieldValue) => {
    const selectedExpenseTypeId = event.target.value;
    setFieldValue("expenseTypeId", selectedExpenseTypeId);
    setFieldValue("accountId", ""); // Reset account
    setAccounts([]); // Clear previous accounts

    await loadAccountsForExpenseType(selectedExpenseTypeId);
  };

  // Sync parent Claim ID
  useEffect(() => {
    if (!isEditing && parentClaimId) {
      setInitialValues((prev) => ({
        ...prev,
        applicationId: parentClaimId,
      }));
    }
    if (isEditing && data) {
      setInitialValues((prev) => ({
        ...prev,
        applicationId: data.applicationId || parentClaimId || "",
      }));

      loadAccountsForExpenseType(data.expenseTypeId);
    }
  }, [isEditing, data, parentClaimId, loadAccountsForExpenseType]);

  // Validation Schema
  const validationSchema = yup.object().shape({
    expenseTypeId: yup.string().required("Expense Type is required"),
    accountId: yup.string().required("Account is required"),
    description: yup.string().required("Description is required"),
    amount: yup
      .number()
      .required("Amount is required")
      .positive("Amount must be greater than zero")
      .typeError("Must be a valid number"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        applicationId: parseInt(parentClaimId || values.applicationId),
        expenseTypeId: parseInt(values.expenseTypeId),
        accountId: parseInt(values.accountId),
        amount: parseFloat(values.amount),
        createdBy: userId,
        isActive: values.isActive ? 1 : 0,
      };

      // Clean up
      if (!isEditing && mutationData.id === null) {
        delete mutationData.id;
      }

      const mutationName = isEditing
        ? "updateClaimLine"
        : "createClaimLine";

      const responseKey = "claimLine";

      const response = await generateAndExecuteMutation(
        mutationName,
        mutationData,
        responseKey,
        hr_base_url
      );

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Claim line ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => refetchData());
      }
    } catch (error) {
      console.error("Error submitting claim line:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to save claim line. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Claim Line" : "Create Claim Line"}
        subtitle={
          isEditing
            ? "Update claim application line"
            : "Add a new line to claim application"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
              }}
            >
              {/* Expense Type Dropdown */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Expense Type *</InputLabel>
                <Select
                  name="expenseTypeId"
                  value={values.expenseTypeId}
                  onChange={(e) => handleExpenseTypeChange(e, setFieldValue)}
                  onBlur={handleBlur}
                  error={touched.expenseTypeId && !!errors.expenseTypeId}
                >
                  <MenuItem value="">
                    <em>Select Expense Type</em>
                  </MenuItem>
                  {expenseTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.expenseTypeId && errors.expenseTypeId && (
                  <Typography color="error" variant="caption">
                    {errors.expenseTypeId}
                  </Typography>
                )}
              </FormControl>

              {/* Account Dropdown (Dependent) */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Account *</InputLabel>
                <Select
                  name="accountId"
                  value={values.accountId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountId && !!errors.accountId}
                  disabled={!values.expenseTypeId}
                >
                  <MenuItem value="">
                    <em>Select Account</em>
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.accountId && errors.accountId && (
                  <Typography color="error" variant="caption">
                    {errors.accountId}
                  </Typography>
                )}
              </FormControl>

              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                label="Description *"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Requested Amount */}
              <TextField
                fullWidth
                variant="filled"
                label="Amount *"
                name="amount"
                type="number"
                inputProps={{ step: "0.01" }}
                value={values.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px" gap="10px">
              <Button onClick={onClose} color="inherit" variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditing
                  ? "Update"
                  : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default StaffClaimApplicationLineForm;
